import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, Paperclip, FileIcon, Loader2 } from "lucide-react";
import { useMudancaChat } from "@/hooks/useMudancaChat";
import { useChatFileUpload } from "@/hooks/useChatFileUpload";
import { useToast } from "@/hooks/use-toast";
import type { Mudanca } from "@/types/mudanca";

interface MudancaChatProps {
  mudanca: Mudanca;
  currentUserId: string;
  currentUserRole: string;
}

export const MudancaChat: React.FC<MudancaChatProps> = ({
  mudanca,
  currentUserId,
  currentUserRole,
}) => {
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const { chatMessages, isLoading, sendMessage, sendMessageWithFile } = useMudancaChat(mudanca.id);
  const { uploadFile, uploading: isUploading } = useChatFileUpload();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!message.trim() && !selectedFile) return;

    const autorTipo = currentUserRole === "user" ? "cliente" : "analista";

    try {
      if (selectedFile) {
        const fileUrl = await uploadFile(selectedFile, mudanca.id);
        if (fileUrl) {
          await sendMessageWithFile.mutateAsync({
            mudanca_id: mudanca.id,
            criado_por: currentUserId,
            autor_tipo: autorTipo,
            mensagem: message.trim() || "Arquivo anexado",
            arquivo_url: fileUrl,
            tipo_arquivo: selectedFile.type,
          });
        }
      } else {
        await sendMessage.mutateAsync({
          mudanca_id: mudanca.id,
          criado_por: currentUserId,
          autor_tipo: autorTipo,
          mensagem: message.trim(),
        });
      }

      setMessage("");
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar a mensagem. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[500px]">
      <div className="flex-1 overflow-y-auto p-4 space-y-4 border rounded-lg bg-muted/20">
        {isLoading && (
          <div className="text-center text-muted-foreground">Carregando mensagens...</div>
        )}
        
        {chatMessages.length === 0 && !isLoading && (
          <div className="text-center text-muted-foreground">
            Nenhuma mensagem ainda. Inicie a conversa!
          </div>
        )}

        {chatMessages.map((msg) => {
          const isOwnMessage = msg.criado_por === currentUserId;
          return (
            <div
              key={msg.id}
              className={`flex gap-3 ${isOwnMessage ? "flex-row-reverse" : "flex-row"}`}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {msg.autor_tipo === "analista" ? "A" : "C"}
                </AvatarFallback>
              </Avatar>
              
              <div className={`flex flex-col ${isOwnMessage ? "items-end" : "items-start"} flex-1`}>
                <div
                  className={`rounded-lg px-4 py-2 max-w-[80%] ${
                    isOwnMessage
                      ? "bg-primary text-primary-foreground"
                      : "bg-background border"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.mensagem}</p>
                  
                  {msg.arquivo_url && (
                    <a
                      href={msg.arquivo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 mt-2 text-xs underline hover:opacity-80"
                    >
                      <FileIcon className="h-4 w-4" />
                      Anexo
                    </a>
                  )}
                </div>
                
                <span className="text-xs text-muted-foreground mt-1">
                  {new Date(msg.criado_em).toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="mt-4 space-y-2">
        {selectedFile && (
          <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
            <FileIcon className="h-4 w-4" />
            <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            >
              Remover
            </Button>
          </div>
        )}

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
          />
          
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Digite sua mensagem... (Shift+Enter para nova linha)"
            className="min-h-[60px] resize-none"
            disabled={sendMessage.isPending || isUploading}
          />

          <Button
            type="button"
            onClick={handleSendMessage}
            disabled={(!message.trim() && !selectedFile) || sendMessage.isPending || isUploading}
            size="icon"
          >
            {sendMessage.isPending || isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
